import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  createDoctor,
  updateDoctor,
  getDoctor,
  getHospitalsForDropdown,
  HospitalDropdown,
} from "../../services/superAdmin.service";
import { Save, Loader2, RotateCcw } from "lucide-react";
import CustomSelect from "../../components/common/CustomSelect";
import {
  getAllPlans,
  getPlanPlatformTypes,
  type SubscriptionPlan,
} from "../../services/subscriptionService";

const INITIAL_FORM = {
  firstName: "",
  lastName: "",
  email: "",
  phoneNumber: "",
  medicalRegistrationNumber: "",
  licenseNumber: "",
  specialization: "",
  department: "",
  qualification: "",
  yearsOfExperience: "",
  hospitalId: "",
  subscriptionPlanId: "",
  status: "Active" as "Active" | "Inactive",
  onboardingStatus: "not started" as "not started" | "in progress" | "completed",
};

const DoctorForm = () => {
  const navigate = useNavigate();
  const { userId } = useParams<{ userId: string }>();
  const isEdit = Boolean(userId);

  const [form, setForm] = useState(INITIAL_FORM);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [hospitals, setHospitals] = useState<HospitalDropdown[]>([]);
  const [hospitalsLoading, setHospitalsLoading] = useState(true);

  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(true);

  // Load subscription plans
  useEffect(() => {
    getAllPlans()
      .then((data) => setPlans(Array.isArray(data) ? data : []))
      .catch(() => toast.error("Failed to load subscription plans"))
      .finally(() => setPlansLoading(false));
  }, []);

  const doctorPlans = plans.filter(
    (plan) => plan.isActive && getPlanPlatformTypes(plan).includes("doctor")
  );

  // Load hospitals dropdown
  useEffect(() => {
    getHospitalsForDropdown()
      .then(setHospitals)
      .catch(() => toast.error("Failed to load hospitals"))
      .finally(() => setHospitalsLoading(false));
  }, []);

  // Load existing doctor data for edit
  useEffect(() => {
    if (!isEdit || !userId) return;
    setFetching(true);
    getDoctor(Number(userId))
      .then((doc) => {
        setForm({
          firstName: doc.firstName || "",
          lastName: doc.lastName || "",
          email: doc.email || "",
          phoneNumber: doc.phoneNumber || "",
          medicalRegistrationNumber: doc.medicalRegistrationNumber || "",
          licenseNumber: doc.licenseNumber || "",
          specialization: doc.specialization || "",
          department: doc.department || "",
          qualification: doc.qualification || "",
          yearsOfExperience: String(doc.yearsOfExperience ?? ""),
          hospitalId: doc.hospitalId ? String(doc.hospitalId) : "",
          subscriptionPlanId: "",
          status: doc.active ? "Active" : "Inactive",
          onboardingStatus: (doc.onboardingStatus as any) || "not started",
        });
      })
      .catch(() => {
        toast.error("Failed to load doctor details");
        navigate("/doctors");
      })
      .finally(() => setFetching(false));
  }, [isEdit, userId, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleReset = () => {
    if (isEdit) {
      // Reload from server in edit mode
      if (userId) {
        getDoctor(Number(userId)).then((doc) => {
          setForm({
            firstName: doc.firstName || "",
            lastName: doc.lastName || "",
            email: doc.email || "",
            phoneNumber: doc.phoneNumber || "",
            medicalRegistrationNumber: doc.medicalRegistrationNumber || "",
            licenseNumber: doc.licenseNumber || "",
            specialization: doc.specialization || "",
            department: doc.department || "",
            qualification: doc.qualification || "",
            yearsOfExperience: String(doc.yearsOfExperience ?? ""),
            hospitalId: doc.hospitalId ? String(doc.hospitalId) : "",
            subscriptionPlanId: "",
            status: doc.active ? "Active" : "Inactive",
            onboardingStatus: (doc.onboardingStatus as any) || "not started",
          });
        });
      }
    } else {
      setForm(INITIAL_FORM);
    }
    setErrors({});
  };

  const validate = (): boolean => {
    const errs: Record<string, string> = {};

    if (!form.firstName.trim()) errs.firstName = "First name is required";
    else if (form.firstName.trim().length > 100) errs.firstName = "Must be 100 characters or less";

    if (!form.lastName.trim()) errs.lastName = "Last name is required";
    else if (form.lastName.trim().length > 100) errs.lastName = "Must be 100 characters or less";

    if (!form.email.trim()) errs.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      errs.email = "Invalid email format";

    if (!form.phoneNumber.trim()) errs.phoneNumber = "Phone number is required";
    else if (!/^[\+]?[\d\s\-()]{7,20}$/.test(form.phoneNumber.trim()))
      errs.phoneNumber = "Invalid phone number format";

    if (!form.medicalRegistrationNumber.trim())
      errs.medicalRegistrationNumber = "Medical Registration Number is required";

    if (!form.licenseNumber.trim()) errs.licenseNumber = "License Number is required";

    if (!form.specialization.trim()) errs.specialization = "Specialization is required";

    if (!form.department.trim()) errs.department = "Department is required";

    if (!form.qualification.trim()) errs.qualification = "Qualification is required";

    if (!form.yearsOfExperience && form.yearsOfExperience !== "0")
      errs.yearsOfExperience = "Years of experience is required";
    else {
      const yoe = Number(form.yearsOfExperience);
      if (isNaN(yoe) || yoe < 0 || yoe > 70)
        errs.yearsOfExperience = "Must be between 0 and 70";
    }

    if (!form.hospitalId) errs.hospitalId = "Hospital is required";

    if (!isEdit && !form.subscriptionPlanId) errs.subscriptionPlanId = "Subscription plan is required";

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const payload = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        phoneNumber: form.phoneNumber.trim(),
        medicalRegistrationNumber: form.medicalRegistrationNumber.trim(),
        licenseNumber: form.licenseNumber.trim(),
        specialization: form.specialization.trim(),
        department: form.department.trim(),
        qualification: form.qualification.trim(),
        yearsOfExperience: Number(form.yearsOfExperience),
        hospitalId: Number(form.hospitalId),
        status: form.status,
        ...(isEdit ? { onboardingStatus: form.onboardingStatus } : { subscriptionPlanId: form.subscriptionPlanId }),
      };

      if (isEdit && userId) {
        await updateDoctor(Number(userId), payload);
        toast.success("Doctor updated successfully");
      } else {
        await createDoctor(payload);
        toast.success("Doctor onboarded successfully");
      }
      navigate("/doctors");
    } catch (err: any) {
      const msg = err?.response?.data?.message || `Failed to ${isEdit ? "update" : "create"} doctor`;
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1.5 text-sm text-gray-500">
        <button onClick={() => navigate("/doctors")} className="hover:text-gray-900 transition-colors">
          Doctors
        </button>
        <span className="text-gray-400">/</span>
        <span className="text-gray-900 font-medium">{isEdit ? "Edit" : "Add New"}</span>
      </nav>

      <form onSubmit={handleSubmit}>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 space-y-5">
          {/* Personal Information */}
          <div>
            <h2 className="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wider">Personal Information</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <FormField label="First Name" name="firstName" value={form.firstName} onChange={handleChange} error={errors.firstName} required />
              <FormField label="Last Name" name="lastName" value={form.lastName} onChange={handleChange} error={errors.lastName} required />
              <FormField label="Email" name="email" value={form.email} onChange={handleChange} error={errors.email} required type="email" />
              <FormField label="Phone Number" name="phoneNumber" value={form.phoneNumber} onChange={handleChange} error={errors.phoneNumber} required />
            </div>
          </div>

          <hr className="border-gray-100" />

          {/* Professional Details */}
          <div>
            <h2 className="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wider">Professional Details</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <FormField label="Medical Registration Number" name="medicalRegistrationNumber" value={form.medicalRegistrationNumber} onChange={handleChange} error={errors.medicalRegistrationNumber} required />
              <FormField label="License Number" name="licenseNumber" value={form.licenseNumber} onChange={handleChange} error={errors.licenseNumber} required />
              <FormField label="Specialization" name="specialization" value={form.specialization} onChange={handleChange} error={errors.specialization} required placeholder="e.g. Oncology" />
              <FormField label="Department" name="department" value={form.department} onChange={handleChange} error={errors.department} required placeholder="e.g. Clinical Hematology" />
              <FormField label="Qualification" name="qualification" value={form.qualification} onChange={handleChange} error={errors.qualification} required placeholder="e.g. MBBS, MD" />
              <FormField label="Years of Experience" name="yearsOfExperience" value={form.yearsOfExperience} onChange={handleChange} error={errors.yearsOfExperience} required type="number" placeholder="e.g. 8" />
            </div>
          </div>

          <hr className="border-gray-100" />

          {/* Assignment */}
          <div>
            <h2 className="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wider">Assignment</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {!isEdit && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Subscription Plan <span className="text-red-500 ml-0.5">*</span>
                  </label>
                  {plansLoading ? (
                    <div className="h-10 bg-gray-50 border border-gray-200 rounded-lg flex items-center justify-center">
                      <Loader2 size={16} className="animate-spin text-gray-400" />
                    </div>
                  ) : (
                    <CustomSelect
                      value={form.subscriptionPlanId}
                      onChange={(val) => {
                        setForm((prev) => ({ ...prev, subscriptionPlanId: val }));
                        if (errors.subscriptionPlanId) {
                          setErrors((prev) => {
                            const next = { ...prev };
                            delete next.subscriptionPlanId;
                            return next;
                          });
                        }
                      }}
                      options={doctorPlans.map((p) => ({
                        value: p.id,
                        label: `${p.planName} (${p.price} ${p.currency} / ${p.durationValue} ${p.durationType})`,
                      }))}
                      placeholder="-- Select Subscription Plan --"
                    />
                  )}
                  {errors.subscriptionPlanId && (
                    <p className="text-xs text-red-600 mt-1">{errors.subscriptionPlanId}</p>
                  )}
                  {!plansLoading && doctorPlans.length === 0 && (
                    <p className="text-xs text-amber-600 mt-1">
                      No active doctor subscription plans configured. Add one under Subscriptions.
                    </p>
                  )}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Hospital <span className="text-red-500 ml-0.5">*</span>
                </label>
                {hospitalsLoading ? (
                  <div className="h-10 bg-gray-50 border border-gray-200 rounded-lg flex items-center justify-center">
                    <Loader2 size={16} className="animate-spin text-gray-400" />
                  </div>
                ) : (
                  <CustomSelect
                    value={form.hospitalId}
                    onChange={(val) => {
                      setForm((prev) => ({ ...prev, hospitalId: val }));
                      if (errors.hospitalId) {
                        setErrors((prev) => {
                          const next = { ...prev };
                          delete next.hospitalId;
                          return next;
                        });
                      }
                    }}
                    options={hospitals.map((h) => ({
                      value: String(h.id),
                      label: h.hospitalName,
                    }))}
                    placeholder="-- Select Hospital --"
                  />
                )}
                {errors.hospitalId && <p className="text-xs text-red-600 mt-1">{errors.hospitalId}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status <span className="text-red-500 ml-0.5">*</span>
                </label>
                <CustomSelect
                  value={form.status}
                  onChange={(val) => setForm((prev) => ({ ...prev, status: val as "Active" | "Inactive" }))}
                  options={[
                    { value: "Active", label: "Active" },
                    { value: "Inactive", label: "Inactive" },
                  ]}
                />
              </div>
              {isEdit && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Onboarding Status
                  </label>
                  <CustomSelect
                    value={form.onboardingStatus}
                    onChange={(val) => setForm((prev) => ({ ...prev, onboardingStatus: val as any }))}
                    options={[
                      { value: "not started", label: "Not Started" },
                      { value: "in progress", label: "In Progress" },
                      { value: "completed", label: "Completed" },
                    ]}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <hr className="border-gray-100" />
          <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-3">
            <button
              type="button"
              onClick={handleReset}
              className="flex items-center justify-center gap-2 px-5 py-2 rounded-lg text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 transition-colors w-full sm:w-auto"
            >
              <RotateCcw size={16} />
              Reset
            </button>
            <button
              type="button"
              onClick={() => navigate("/doctors")}
              className="px-5 py-2 rounded-lg text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 transition-colors w-full sm:w-auto"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center justify-center gap-2 bg-primary-600 text-white px-5 py-2 rounded-lg text-sm font-medium shadow-sm hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors w-full sm:w-auto"
            >
              {loading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  {isEdit ? "Updating..." : "Creating..."}
                </>
              ) : (
                <>
                  <Save size={16} />
                  {isEdit ? "Update Doctor" : "Create Doctor"}
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

// --- Helper Component ---

const FormField = ({
  label,
  name,
  value,
  onChange,
  error,
  required,
  type = "text",
  placeholder,
  className,
}: {
  label: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  error?: string;
  required?: boolean;
  type?: string;
  placeholder?: string;
  className?: string;
}) => (
  <div className={className}>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label}
      {required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors ${
        error ? "border-red-300 bg-red-50" : "border-gray-200"
      }`}
    />
    {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
  </div>
);

export default DoctorForm;
